/**
 * ERP record-provenance API client.
 *
 * Read-only: "does an ERP sync claim this MDM record, and which of its fields
 * does that sync write?" Served by the ERP domain rather than MDM — MDM must
 * not learn to read ERP for a display concern.
 */

import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import type {ErpRecordProvenance, ErpSyncedType} from '../types';

/* eslint-disable @typescript-eslint/naming-convention */
type RawErpRecordProvenance = {
  is_synced: boolean;
  source_system: string | null;
  entity_kind: string | null;
  ownership: string | null;
  external_id: string | null;
  last_synced_at: string | null;
  sync_written_field_keys: string[];
  writable_field_keys: string[];
};

type RawBatchErpRecordProvenance = {
  provenance: Record<string, RawErpRecordProvenance>;
};

type RawErpSyncedType = {
  source_system: string;
  entity_kind: string;
  target_object_type_id: string;
  last_synced_at: string | null;
  last_sync_status: string;
};

type RawErpSyncedTypes = {items: RawErpSyncedType[]};
/* eslint-enable @typescript-eslint/naming-convention */

function convertProvenance(raw: RawErpRecordProvenance): ErpRecordProvenance {
  return {
    isSynced: raw.is_synced,
    sourceSystem: raw.source_system ?? undefined,
    entityKind: raw.entity_kind ?? undefined,
    ownership:
      raw.ownership === 'sync_created' || raw.ownership === 'adopted'
        ? raw.ownership
        : undefined,
    externalId: raw.external_id ?? undefined,
    lastSyncedAt: raw.last_synced_at ?? undefined,
    syncWrittenFieldKeys: raw.sync_written_field_keys ?? [],
    writableFieldKeys: raw.writable_field_keys ?? [],
  };
}

export class ErpRecordProvenanceApiClient extends AuthenticatedApiClient {
  private get basePath(): string {
    return '/api/v1/erp/integrations/cetec';
  }

  async getRecordProvenance(recordId: string): Promise<ErpRecordProvenance> {
    const raw = await this.get<RawErpRecordProvenance>(
      `${this.basePath}/record-provenance/${recordId}`
    );
    return convertProvenance(raw);
  }

  /**
   * Claims across a page of records, in one call.
   *
   * POST because the payload is a page of UUIDs; it reads and writes
   * nothing. Unclaimed records are absent from the response — a caller
   * treats a missing id as unclaimed, which is the ordinary case.
   */
  async batchGetRecordProvenance(
    recordIds: string[]
  ): Promise<Map<string, ErpRecordProvenance>> {
    if (recordIds.length === 0) return new Map();
    const raw = await this.request<RawBatchErpRecordProvenance>(
      `${this.basePath}/record-provenance/batch`,
      {
        method: 'POST',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        body: JSON.stringify({object_record_ids: recordIds}),
      }
    );
    return new Map(
      Object.entries(raw.provenance).map(([recordId, value]) => [
        recordId,
        convertProvenance(value),
      ])
    );
  }

  /** Which ObjectTypes an ERP sync feeds. Safe for every role. */
  async listSyncedTypes(): Promise<Map<string, ErpSyncedType>> {
    const raw = await this.get<RawErpSyncedTypes>(
      `${this.basePath}/synced-types`
    );
    return convertSyncedTypes(raw);
  }
}

/** Keyed by ObjectType id — the shape every caller wants. */
function convertSyncedTypes(
  raw: RawErpSyncedTypes
): Map<string, ErpSyncedType> {
  return new Map(
    raw.items.map((item) => [
      item.target_object_type_id,
      {
        sourceSystem: item.source_system,
        entityKind: item.entity_kind,
        targetObjectTypeId: item.target_object_type_id,
        lastSyncedAt: item.last_synced_at ?? undefined,
        lastSyncStatus:
          item.last_sync_status === 'success' ||
          item.last_sync_status === 'error'
            ? item.last_sync_status
            : 'never_run',
      },
    ])
  );
}

let cachedErpRecordProvenanceApi: ErpRecordProvenanceApiClient | undefined;

export function getErpRecordProvenanceApi(
  token?: string
): ErpRecordProvenanceApiClient {
  if (token) return new ErpRecordProvenanceApiClient(token);
  cachedErpRecordProvenanceApi ??= new ErpRecordProvenanceApiClient();
  return cachedErpRecordProvenanceApi;
}
